/**
 * Pure quote helpers (totals, VAT, rounding, numbering, status) shared by the
 * admin quote builder and the public quote page. No React / Supabase imports so
 * they can be unit tested directly.
 *
 * Money rules (mirrored by `respond_to_quote` in the priced-quotes migration):
 *   * each line total = round(qty × unit price, 2)
 *   * subtotal = sum of line totals (required lines + any optional extras the
 *     customer has picked)
 *   * VAT = round(subtotal × rate / 100, 2), only when the business is VAT
 *     registered — otherwise prices are the full price as entered
 *   * total = subtotal + VAT
 */

export type QuoteStatus = 'draft' | 'sent' | 'viewed' | 'accepted' | 'declined' | 'expired';

export const QUOTE_STATUSES: { value: QuoteStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'expired', label: 'Expired' },
];

export const DEFAULT_VAT_RATE = 20;
export const DEFAULT_VALIDITY_DAYS = 30;

export interface QuoteLineLike {
  quantity?: number | string | null;
  unit_amount?: number | string | null;
  is_optional?: boolean | null;
  is_selected?: boolean | null;
}

export interface QuoteTotals {
  subtotal: number;
  vat: number;
  total: number;
  /** Sum of optional extras the customer hasn't picked (shown as "+ £x if added"). */
  optionalAvailable: number;
}

/** Round to pennies, avoiding binary float drift (1.005 → 1.01). */
export function roundMoney(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const sign = value < 0 ? -1 : 1;
  return (sign * Math.round((Math.abs(value) + Number.EPSILON) * 100)) / 100;
}

const toNumber = (value: number | string | null | undefined): number => {
  const n = typeof value === 'string' ? Number.parseFloat(value) : Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export function lineTotal(line: Pick<QuoteLineLike, 'quantity' | 'unit_amount'>): number {
  return roundMoney(toNumber(line.quantity) * toNumber(line.unit_amount));
}

/** Clamp a VAT rate to 0–100; anything unusable falls back to 0. */
export function normaliseVatRate(rate: number | string | null | undefined): number {
  const n = toNumber(rate);
  if (n <= 0) return 0;
  return Math.min(100, roundMoney(n));
}

export function vatFor(subtotal: number, vatRate: number | string | null | undefined): number {
  const rate = normaliseVatRate(vatRate);
  if (rate === 0) return 0;
  return roundMoney((subtotal * rate) / 100);
}

/** Whether a line counts towards the quote total right now. */
export function lineCounts(line: QuoteLineLike): boolean {
  return !line.is_optional || Boolean(line.is_selected);
}

export function computeQuoteTotals(
  lines: QuoteLineLike[],
  vatRate: number | string | null | undefined,
): QuoteTotals {
  let subtotal = 0;
  let optionalAvailable = 0;
  for (const line of lines) {
    const total = lineTotal(line);
    if (lineCounts(line)) subtotal += total;
    else optionalAvailable += total;
  }
  subtotal = roundMoney(subtotal);
  const vat = vatFor(subtotal, vatRate);
  return {
    subtotal,
    vat,
    total: roundMoney(subtotal + vat),
    optionalAvailable: roundMoney(optionalAvailable),
  };
}

/** The VAT rate a new quote starts with, from the org's settings. */
export function vatRateForOrg(settings: { vat_registered?: boolean | null; vat_rate?: number | string | null } | null | undefined): number {
  if (!settings?.vat_registered) return 0;
  const rate = settings.vat_rate == null ? DEFAULT_VAT_RATE : normaliseVatRate(settings.vat_rate);
  return rate;
}

// --- Numbering --------------------------------------------------------------

/** 1 → "Q-0001"; grows past four digits rather than wrapping (10000 → "Q-10000"). */
export function formatQuoteNumber(seq: number): string {
  const n = Math.max(1, Math.floor(seq));
  return `Q-${String(n).padStart(4, '0')}`;
}

/** "Q-0042" → 42; null for anything that isn't a quote number. */
export function parseQuoteNumber(value: string | null | undefined): number | null {
  const match = /^Q-(\d+)$/i.exec((value ?? '').trim());
  if (!match) return null;
  const n = Number.parseInt(match[1], 10);
  return n > 0 ? n : null;
}

/** Next number after the highest existing one (gaps are never reused). */
export function nextQuoteNumber(existing: Array<string | null | undefined>): string {
  const max = existing.reduce<number>((acc, value) => Math.max(acc, parseQuoteNumber(value) ?? 0), 0);
  return formatQuoteNumber(max + 1);
}

// --- Status -----------------------------------------------------------------

const toIsoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** True once the valid-until date (inclusive) is behind us. */
export function isQuotePastValidity(validUntil: string | null | undefined, now: Date = new Date()): boolean {
  if (!validUntil) return false;
  return validUntil.slice(0, 10) < toIsoDate(now);
}

/**
 * Status to show: a sent/viewed quote past its valid-until date reads as
 * expired even before the server has flipped it.
 */
export function effectiveQuoteStatus(
  status: QuoteStatus,
  validUntil: string | null | undefined,
  now: Date = new Date(),
): QuoteStatus {
  if ((status === 'sent' || status === 'viewed') && isQuotePastValidity(validUntil, now)) return 'expired';
  return status;
}

/** A customer may still accept or decline. */
export function canRespondToQuote(status: QuoteStatus, validUntil: string | null | undefined, now: Date = new Date()): boolean {
  const effective = effectiveQuoteStatus(status, validUntil, now);
  return effective === 'sent' || effective === 'viewed';
}

/** Default valid-until date (today + N days) as yyyy-MM-dd. */
export function defaultValidUntil(now: Date = new Date(), days = DEFAULT_VALIDITY_DAYS): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  return toIsoDate(d);
}

/** Enquiry frequency values that mean "not given" (callbacks carry a dash). */
export function cleanFrequency(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  if (!v || v === '—' || v === '-') return null;
  return v;
}

/** Public quote path on the tenant site. */
export const quotePath = (token: string) => `/quote/${token}`;

export interface BookFromQuoteInput {
  id: string;
  quote_number: string;
  client_name: string;
  client_email?: string | null;
  client_phone?: string | null;
  client_address?: string | null;
  frequency?: string | null;
  service_id?: string | null;
  title?: string | null;
}

/**
 * Link into the existing Add Job dialog (/admin/schedule?new=job) with the
 * quote's details prefilled. `frequency` is passed straight through for the
 * recurring-jobs flow.
 */
export function bookFromQuoteUrl(quote: BookFromQuoteInput): string {
  const params = new URLSearchParams({ new: 'job', fromQuote: quote.id });
  params.set('title', quote.title || `${quote.client_name} — ${quote.quote_number}`);
  params.set('client_name', quote.client_name);
  if (quote.client_email) params.set('client_email', quote.client_email);
  if (quote.client_phone) params.set('client_phone', quote.client_phone);
  if (quote.client_address) params.set('address', quote.client_address);
  if (quote.service_id) params.set('service_id', quote.service_id);
  const frequency = cleanFrequency(quote.frequency);
  if (frequency) params.set('frequency', frequency);
  return `/admin/schedule?${params.toString()}`;
}

export const JOB_PREFILL_KEYS = [
  'fromQuote', 'title', 'client_name', 'client_email', 'client_phone', 'address', 'service_id', 'frequency',
] as const;

export interface JobPrefill {
  quoteId: string | null;
  frequency: string | null;
  fields: Partial<Record<'title' | 'client_name' | 'client_email' | 'client_phone' | 'address' | 'service_id' | 'notes', string>>;
}

/** Read the Add Job prefill written by bookFromQuoteUrl; null when there is none. */
export function readJobPrefill(params: URLSearchParams): JobPrefill | null {
  const quoteId = params.get('fromQuote');
  const frequency = cleanFrequency(params.get('frequency'));
  const fields: JobPrefill['fields'] = {};
  for (const key of ['title', 'client_name', 'client_email', 'client_phone', 'address', 'service_id'] as const) {
    const value = params.get(key);
    if (value) fields[key] = value.slice(0, 500);
  }
  if (!quoteId && Object.keys(fields).length === 0) return null;
  if (frequency) fields.notes = `Frequency: ${frequency}`;
  return { quoteId, frequency, fields };
}

const CURRENCY_SYMBOL: Record<string, string> = { GBP: '£', EUR: '€', USD: '$' };

/** Always two decimals on quotes: £1,234.50. */
export function formatQuoteMoney(amount: number | string | null | undefined, currencyCode = 'GBP'): string {
  const value = roundMoney(toNumber(amount));
  const symbol = CURRENCY_SYMBOL[currencyCode] ?? '£';
  const formatted = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(value));
  return `${value < 0 ? '-' : ''}${symbol}${formatted}`;
}

/** 20 → "20%", 12.5 → "12.5%". */
export const formatVatRate = (rate: number | string | null | undefined) => `${normaliseVatRate(rate)}%`;
